/**
 * Card View Configuration Service
 * Manages global card view settings stored in S3
 */

import { CardViewConfig } from '../types';
import { apiClient, apiEndpoints } from './apiClient';

// Check if API Gateway is configured (production mode)
const isApiGatewayConfigured = (): boolean => {
  return !!process.env.REACT_APP_API_URL;
};

// Default card view configuration
const DEFAULT_CARD_VIEW_CONFIG: CardViewConfig = {
  id: 'default',
  version: '1.0',
  showThumbnail: true,
  showDocumentId: true,
  showFileName: true,
  showStatusBadge: true,
  fields: [
    {
      id: 'vendor',
      type: 'stored',
      fieldName: 'Vendor',
      label: 'Vendor',
      icon: 'Building2',
      enabled: true,
      order: 1,
      fallbackFieldNames: ['Vendor Name'],
    },
    {
      id: 'amount',
      type: 'stored',
      fieldName: 'Amount',
      label: 'Amount',
      icon: 'DollarSign',
      enabled: true,
      order: 2,
      fallbackFieldNames: ['Total Amount', 'Total'],
    },
    {
      id: 'date',
      type: 'stored',
      fieldName: 'Date',
      label: 'Date',
      icon: 'Calendar',
      enabled: true,
      order: 3,
    },
  ],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

let cachedConfig: CardViewConfig | null = null;

/**
 * Get card view configuration
 */
export async function getCardViewConfig(): Promise<CardViewConfig> {
  try {
    // Use API Gateway if configured (production mode)
    if (isApiGatewayConfigured()) {
      const response = await apiClient.get<{ config: CardViewConfig }>(apiEndpoints.cardView.get());
      if (response.error || !response.data?.config) {
        console.warn('Error loading card view config from API, using default:', response.error);
        return DEFAULT_CARD_VIEW_CONFIG;
      }
      cachedConfig = response.data.config;
      return response.data.config;
    }

    // In local development, return default (or could load from localStorage)
    const stored = localStorage.getItem('cardViewConfig');
    if (stored) {
      try {
        const config = JSON.parse(stored);
        cachedConfig = config;
        return config;
      } catch (e) {
        console.warn('Failed to parse stored card view config, using default');
      }
    }

    return DEFAULT_CARD_VIEW_CONFIG;
  } catch (error: any) {
    console.warn('Error loading card view config (non-critical):', error);
    return DEFAULT_CARD_VIEW_CONFIG;
  }
}

/**
 * Save card view configuration
 */
export async function saveCardViewConfig(config: CardViewConfig): Promise<CardViewConfig> {
  try {
    const updatedConfig: CardViewConfig = {
      ...config,
      updatedAt: new Date().toISOString(),
    };

    // Use API Gateway if configured (production mode)
    if (isApiGatewayConfigured()) {
      const response = await apiClient.put<{ config: CardViewConfig }>(
        apiEndpoints.cardView.update(),
        updatedConfig
      );
      if (response.error) {
        throw new Error(response.error);
      }
      if (!response.data?.config) {
        throw new Error('Failed to save card view config');
      }
      cachedConfig = response.data.config;
      return response.data.config;
    }

    // In local development, save to localStorage
    localStorage.setItem('cardViewConfig', JSON.stringify(updatedConfig));
    cachedConfig = updatedConfig;
    return updatedConfig;
  } catch (error: any) {
    console.error('Error saving card view config:', error);
    throw error;
  }
}

/**
 * Get cached config (synchronous, for immediate use)
 */
export function getCachedCardViewConfig(): CardViewConfig | null {
  return cachedConfig;
}

/**
 * Reset to default configuration
 */
export async function resetCardViewConfig(): Promise<CardViewConfig> {
  return await saveCardViewConfig(DEFAULT_CARD_VIEW_CONFIG);
}

export const cardViewConfigService = {
  getCardViewConfig,
  saveCardViewConfig,
  getCachedCardViewConfig,
  resetCardViewConfig,
};

